import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadCloud, Loader2, Trash2, GripVertical, ImagePlus } from 'lucide-react';
import { useApi } from '@/hooks/use-api';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';

const MAX_IMAGES = 10;
const CHUNK = 8 * 1024 * 1024;

/**
 * Product Images uploader — multi-select, drag-to-reorder, capped at MAX_IMAGES.
 *
 * `value` is an array of `{ url, caption? }` entries. Emits `onChange(nextArr)`
 * after every mutation so the parent can persist. Reuses the same
 * `/api/uploads/multipart/*` pipeline as `FileUpload` so we don't need any new
 * backend endpoints.
 *
 * Props:
 *   value        — current array of images
 *   onChange     — receives the next array
 *   folder       — R2 folder namespace (default: "ventures")
 *   testidPrefix — namespace for data-testids (default: "product-images")
 */
export const ProductImages = ({ value = [], onChange, folder = 'ventures', testidPrefix = 'product-images' }) => {
  const api = useApi();
  const { token, API } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: null });
  const [dragIx, setDragIx] = useState(null);

  const remaining = Math.max(0, MAX_IMAGES - value.length);

  const putPart = (key, uploadId, partNumber, blob) => new Promise((resolve, reject) => {
    const qs = `key=${encodeURIComponent(key)}&upload_id=${encodeURIComponent(uploadId)}&part_number=${partNumber}`;
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API}/api/uploads/multipart/part?${qs}`, true);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error('Bad response')); }
      } else reject(new Error(`Chunk failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(blob);
  });

  const uploadOne = async (file) => {
    const content_type = file.type || 'application/octet-stream';
    const { key, upload_id } = await api.post('/api/uploads/multipart/start', { filename: file.name, content_type, folder });
    const totalParts = Math.max(1, Math.ceil(file.size / CHUNK));
    const parts = [];
    for (let i = 0; i < totalParts; i++) {
      const start = i * CHUNK;
      const blob = file.slice(start, Math.min(start + CHUNK, file.size));
      const res = await putPart(key, upload_id, i + 1, blob);
      parts.push({ PartNumber: res.PartNumber, ETag: res.ETag });
    }
    const doc = await api.post('/api/uploads/multipart/complete', { key, upload_id, parts, filename: file.name, content_type, size: file.size, folder });
    return api.publicUrl(doc.key);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    if (!files.length) return;
    const room = MAX_IMAGES - value.length;
    if (room <= 0) { toast.error(`Maximum ${MAX_IMAGES} images reached`); return; }
    const batch = files.slice(0, room);
    if (batch.length < files.length) toast.message(`Only ${batch.length} image(s) fit`, { description: `You can have up to ${MAX_IMAGES} product images.` });
    setBusy(true);
    setProgress({ done: 0, total: batch.length, current: batch[0].name });
    const uploaded = [];
    for (let i = 0; i < batch.length; i++) {
      const f = batch[i];
      setProgress({ done: i, total: batch.length, current: f.name });
      try {
        const url = await uploadOne(f);
        uploaded.push({ url, caption: '' });
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`, { description: err.message });
      }
    }
    if (uploaded.length) {
      onChange([...value, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? '' : 's'} uploaded`);
    }
    setBusy(false);
    setProgress({ done: 0, total: 0, current: null });
  };

  const remove = (i) => onChange(value.filter((_, ix) => ix !== i));

  // Native HTML5 drag & drop — no extra deps, and it works cleanly for images.
  const onDragStart = (i) => setDragIx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIx == null || dragIx === i) { setDragIx(null); return; }
    const next = value.slice();
    const [m] = next.splice(dragIx, 1);
    next.splice(i, 0, m);
    setDragIx(null);
    onChange(next);
  };

  return (
    <div data-testid={testidPrefix}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          Product Images · {value.length}/{MAX_IMAGES}
        </p>
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={onFiles}
            data-testid={`${testidPrefix}-input`}
          />
          <Button
            type="button" variant="outline" size="sm"
            className="rounded-full gap-1.5"
            disabled={busy || remaining <= 0}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testidPrefix}-add`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
            {busy ? `Uploading ${progress.done + 1}/${progress.total}` : `Add images (${remaining} left)`}
          </Button>
        </div>
      </div>

      {value.length ? (
        <>
          <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1.5">
            <GripVertical className="h-3 w-3" /> Drag to reorder — the first image is the cover.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {value.map((s, i) => (
              <div
                key={`${s.url}-${i}`}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(i)}
                className={`group relative rounded-xl overflow-hidden border-2 aspect-video bg-secondary transition-colors ${dragIx === i ? 'border-primary/60' : 'border-border'}`}
                data-testid={`${testidPrefix}-item-${i}`}
              >
                <img src={s.url} alt={s.caption || `Product image ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                <div className="absolute top-2 left-2 h-6 w-6 rounded-full bg-background/90 border border-border grid place-items-center text-[10px] font-medium">{i + 1}</div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="h-6 w-6 rounded-full bg-background/90 border border-border grid place-items-center cursor-grab" title="Drag to reorder"><GripVertical className="h-3.5 w-3.5" /></span>
                  <button type="button" onClick={() => remove(i)} className="h-6 w-6 rounded-full bg-background/90 border border-border grid place-items-center" data-testid={`${testidPrefix}-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-2xl border border-dashed border-border p-10 text-center hover:bg-secondary/40 transition-colors"
          data-testid={`${testidPrefix}-empty-add`}
        >
          <ImagePlus className="h-6 w-6 mx-auto text-muted-foreground" />
          <p className="text-sm mt-2">Show, don&apos;t tell — add up to {MAX_IMAGES} product images.</p>
          <p className="text-xs text-muted-foreground mt-1">Click to select multiple at once.</p>
        </button>
      )}
    </div>
  );
};

export default ProductImages;
